import { clientLoginPath } from '@api/http.api.ts';
import type { AppDispatchFn } from '@hooks/reduxHooks.ts';
import { doLogout, setIsSessionExpiringSoon } from '@routes/auth/___shared___/auth.slice.ts';
import { cancelTokenRefreshTimer } from '@routes/auth/___shared___/authTokenRefreshScheduler.ts';

// Ignore repeated activity events for 3 seconds to reduce unnecessary event handling.
const ACTIVITY_THROTTLE_MS = 3000;
// Keep the session alive every 30 seconds while media is playing.
const MEDIA_ACTIVITY_INTERVAL_MS = 30000;
// Show the session expiry warning 20 seconds before logout.
export const SESSION_EXPIRY_WARNING_MS = 20 * 1000;

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];

let inactivityTimer: number | null = null;
let mediaActivityInterval: number | null = null;
let lastActivityHandledAt = 0;
let currentTimeoutMs = 0;
let currentDispatch: AppDispatchFn | null = null;
let isInitialized = false;
let isExpiring = false;
let isMediaPlaybackActive = false;
let sessionExpiryWarningTimer: number | null = null;
let isSessionExpiryWarningVisible = false;

// Clears the timer that would automatically log the user out after inactivity.
const clearInactivityTimer = (): void => {
    if (inactivityTimer !== null) {
        window.clearTimeout(inactivityTimer);
        inactivityTimer = null;
    }
};

// Stops the interval that keeps the session active while media is playing.
const clearMediaActivityInterval = (): void => {
    if (mediaActivityInterval !== null) {
        window.clearInterval(mediaActivityInterval);
        mediaActivityInterval = null;
    }
};

// Clears the timer responsible for showing the session expiry warning.
const clearSessionExpiryWarningTimer = (): void => {
    if (sessionExpiryWarningTimer !== null) {
        window.clearTimeout(sessionExpiryWarningTimer);
        sessionExpiryWarningTimer = null;
    }
};

// Updates whether the session is about to expire warning should be shown.
const updateSessionExpiryWarning = (show: boolean): void => {
    if (!currentDispatch || isSessionExpiryWarningVisible === show) return;

    isSessionExpiryWarningVisible = show;
    currentDispatch(setIsSessionExpiringSoon(show));
};

// Logs the user out, stops all session timers, and redirects to the login page.
const expireSession = async (): Promise<void> => {
    if (isExpiring || !currentDispatch) return;

    isExpiring = true;
    clearInactivityTimer();
    clearMediaActivityInterval();
    clearSessionExpiryWarningTimer();

    try {
        cancelTokenRefreshTimer();
        await currentDispatch(doLogout());
        window.location.replace(clientLoginPath);
    } finally {
        isExpiring = false;
    }
};

// Restarts the session expiry and warning timers from the current moment.
const scheduleInactivityTimeout = (): void => {
    clearInactivityTimer();
    clearSessionExpiryWarningTimer();

    if (!isInitialized || currentTimeoutMs <= 0) return;

    updateSessionExpiryWarning(false);

    const warningDelayMs = currentTimeoutMs - SESSION_EXPIRY_WARNING_MS;

    if (warningDelayMs <= 0) {
        updateSessionExpiryWarning(true);
    } else {
        sessionExpiryWarningTimer = window.setTimeout(() => {
            updateSessionExpiryWarning(true);
        }, warningDelayMs);
    }

    inactivityTimer = window.setTimeout(() => {
        void expireSession();
    }, currentTimeoutMs);
};

// Treats mouse, keyboard, touch, or scroll activity as user activity and extends the session.
const handleUserActivity = (): void => {
    const now = Date.now();

    if (now - lastActivityHandledAt < ACTIVITY_THROTTLE_MS) return;

    lastActivityHandledAt = now;
    scheduleInactivityTimeout();
};

// Extends the session when the user returns to the browser tab.
const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
        handleUserActivity();
    }
};

// Starts listening for user activity such as mouse movement, keyboard input, scrolling, and touch.
const addActivityListeners = (): void => {
    ACTIVITY_EVENTS.forEach((eventName) => {
        window.addEventListener(eventName, handleUserActivity, {
            passive: true,
        });
    });

    document.addEventListener('scroll', handleUserActivity, {
        capture: true,
        passive: true,
    });

    document.addEventListener('visibilitychange', handleVisibilityChange);
};

// Stops listening for user activity events.
const removeActivityListeners = (): void => {
    ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserActivity);
    });

    document.removeEventListener('scroll', handleUserActivity, true);

    document.removeEventListener('visibilitychange', handleVisibilityChange);
};

// Starts checking whether media is playing and keeps the session active during playback.
const startMediaActivityTracking = (): void => {
    clearMediaActivityInterval();

    mediaActivityInterval = window.setInterval(() => {
        if (isMediaPlaybackActive) {
            scheduleInactivityTimeout();
        }
    }, MEDIA_ACTIVITY_INTERVAL_MS);
};

// Initializes the inactivity timeout, warning timer, activity listeners, and media tracking.
export const initSessionExpiryScheduler = (
    inactivityTimeoutMinutes: number | null | undefined,
    dispatch: AppDispatchFn,
): void => {
    cancelSessionExpiryScheduler();

    if (
        inactivityTimeoutMinutes === null ||
        inactivityTimeoutMinutes === undefined ||
        !Number.isFinite(inactivityTimeoutMinutes) ||
        inactivityTimeoutMinutes <= 0
    ) {
        return;
    }

    currentTimeoutMs = inactivityTimeoutMinutes * 60 * 1000;
    currentDispatch = dispatch;
    lastActivityHandledAt = Date.now();
    isInitialized = true;

    addActivityListeners();
    startMediaActivityTracking();
    scheduleInactivityTimeout();
};

// Tells the scheduler whether media is currently playing.
export const setExpirySessionMediaPlaybackActive = (isPlaying: boolean): void => {
    isMediaPlaybackActive = isPlaying;

    if (isPlaying && isInitialized) {
        scheduleInactivityTimeout();
    }
};

// Stops the scheduler, removes listeners, clears timers, and resets its internal state.
export const cancelSessionExpiryScheduler = (): void => {
    clearInactivityTimer();
    clearMediaActivityInterval();
    removeActivityListeners();
    clearSessionExpiryWarningTimer();
    updateSessionExpiryWarning(false);

    currentTimeoutMs = 0;
    currentDispatch = null;
    lastActivityHandledAt = 0;
    isMediaPlaybackActive = false;
    isInitialized = false;
    isExpiring = false;
    isSessionExpiryWarningVisible = false;
};
